import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSpring, animated } from "@react-spring/web";
import bombIcon from "./assets/images/bomb.svg";
import smolButtonIcon from "./assets/images/picture-in-picture.png";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseIcon from "@mui/icons-material/Close";
import bounceArrow from "./assets/images/bounceArrow.svg";
import bonVoyageArrow from "./assets/images/bonVoyageArrow.svg";
import arunsDucky from "./assets/images/arunsDucky.svg";
import ruhisPanda from "./assets/images/ruhisPanda.svg";
import sauravsElephant from "./assets/images/sauravsElephant.jpeg";
import boredHumans from "./assets/images/boredHumans.svg";
import dinoImage from "./assets/images/Dino.svg";
import laptopImage from "./assets/images/laptop.svg";
import SubmitLinkPopUp from "./components/SubmitLinkPopUp";
import FeedbackPopUp from "./components/FeedbackPopUp";
import LastVisitedPopup from "./components/LastVisitedPopup";
import CookieConsent from "./components/CookieConsent";
import ManifestoPopup from "./components/ManifestoPopup";
import RightSideCardShell from "./components/RightSideCardShell";
import AnimatedRainbowBorder from "./components/AnimatedRainbowBorder";
import RetroVideoPlayer from "./components/RetroVideoPlayer";
import arunVideo from "./assets/videos/ArunKoushik.webm";
import sauravVideo from "./assets/videos/SauravBanerjee.webm";
import ruhiVideo from "./assets/videos/RuhiPanjwani.webm";
import { Website } from "./types/siteSpecs";
import AdventureButton, {
  AdventureButtonHandle,
} from "./components/AdventureButton";
import Magnet from "./components/Magnet";
import StarField from "./components/StarField";
import { isPipSupported, isPipOpen, openPipWindow, PipHandle } from "./components/launchPipButton";
import { createPortal } from "react-dom";
import PipContent from "./components/PipContent";
import { SITE_URL, withoutHttpUrl } from "./siteUrl";
import { trackEvent } from "./analytics";
import {
  displayPressCount,
  fetchButtonPressCount,
  formatPressCount,
  recordButtonPress,
  recordLinkShown,
} from "./buttonPressCounter";

const SHEET_URL =
  "https://docs.google.com/spreadsheets/d/1IwzYFymsG3qOFELz3iHQE_7b-xul1XzljkaoA1KIrMk/pub?output=csv";
/** X / WhatsApp / Copy link in share popup — see LAUNCH_AUDIT.md marketing snapshot */
const shareInviteBody = (siteUrl: string) =>
  `A love letter to the internet. A big red button that sends you to the coolest corners of the webspace. You're welcome. 🚀\n\no()xxxx[{::::::::::::::::::>\n\n${siteUrl}\n\n<::::::::::::::::::[{xxxx()o`;

// ─── Experiment: Mini Button popup ───────────────────────────
// Set to false to disable the floating PiP button entirely.
const EXPERIMENT_MINI_BUTTON = true;

const RUNWAY_TOP_LEFT = 45;
const RUNWAY_TOP_RIGHT = 55;
const RUNWAY_BOTTOM_LEFT = 30;
const RUNWAY_BOTTOM_RIGHT = 70;
const RUNWAY_BORDER_OFFSET = 0.1;
/** Must match trapezoid SVG viewBox height; spring `height` is in these units, not screen px */
const RUNWAY_TRAP_VIEWBOX = 1000;

type DesktopTier = "compact" | "regular" | "wide";

interface DesktopLayout {
  tier: DesktopTier;
  isCompact: boolean;
  gridTemplateColumns: string;
  sidePanelWidth: string;
  sidePanelPaddingX: string;
  sidePanelCenterPadding: string;
  sidePanelContentMaxWidth: string;
  topLogoWidth: number;
  topLogoHeight: number;
  topLogoTop: number;
  panelLabelSize: string;
  panelTextSize: string;
  panelHeadingSize: string;
  panelActionTextSize: string;
  avatarSize: number;
  headingSize: string;
  headingTranslateY: string;
  headingArrowHeight: number;
  headingArrowWidth: number;
  headingArrowMarginTop: number;
  bonVoyageTop: string;
  bonVoyageLabelSize: string;
  bonVoyageTitleSize: string;
  buttonSize: string;
  buttonMinSize: number;
  buttonTranslateY: string;
  magnetPadding: number;
}

const getDesktopLayout = (viewportWidth: number): DesktopLayout => {
  if (viewportWidth < 1280) {
    return {
      tier: "compact",
      isCompact: true,
      gridTemplateColumns: "minmax(260px, 1fr) minmax(280px, 300px) minmax(260px, 1fr)",
      sidePanelWidth: "320px",
      sidePanelPaddingX: "18px",
      sidePanelCenterPadding: "32px",
      sidePanelContentMaxWidth: "100%",
      topLogoWidth: 144,
      topLogoHeight: 86,
      topLogoTop: 20,
      panelLabelSize: "14px",
      panelTextSize: "14px",
      panelHeadingSize: "17px",
      panelActionTextSize: "13px",
      avatarSize: 38,
      headingSize: "clamp(34px, 3.4vw, 46px)",
      headingTranslateY: "calc(-50% + 36px)",
      headingArrowHeight: 54,
      headingArrowWidth: 40,
      headingArrowMarginTop: 10,
      bonVoyageTop: "38%",
      bonVoyageLabelSize: "clamp(18px, 1.5vw, 24px)",
      bonVoyageTitleSize: "clamp(30px, 2.5vw, 42px)",
      buttonSize: "clamp(232px, 19vw, 286px)",
      buttonMinSize: 240,
      buttonTranslateY: "12%",
      magnetPadding: 78,
    };
  }

  if (viewportWidth < 1500) {
    return {
      tier: "regular",
      isCompact: false,
      gridTemplateColumns: "minmax(280px, 1fr) minmax(340px, 390px) minmax(280px, 1fr)",
      sidePanelWidth: "390px",
      sidePanelPaddingX: "20px",
      sidePanelCenterPadding: "34px",
      sidePanelContentMaxWidth: "100%",
      topLogoWidth: 156,
      topLogoHeight: 94,
      topLogoTop: 22,
      panelLabelSize: "15px",
      panelTextSize: "15px",
      panelHeadingSize: "18px",
      panelActionTextSize: "14px",
      avatarSize: 40,
      headingSize: "clamp(36px, 3.5vw, 52px)",
      headingTranslateY: "calc(-50% + 40px)",
      headingArrowHeight: 58,
      headingArrowWidth: 44,
      headingArrowMarginTop: 12,
      bonVoyageTop: "39%",
      bonVoyageLabelSize: "clamp(19px, 1.6vw, 26px)",
      bonVoyageTitleSize: "clamp(32px, 2.7vw, 46px)",
      buttonSize: "clamp(248px, 20vw, 320px)",
      buttonMinSize: 270,
      buttonTranslateY: "15%",
      magnetPadding: 88,
    };
  }

  return {
    tier: "wide",
    isCompact: false,
    gridTemplateColumns: "minmax(320px, 1fr) minmax(400px, 500px) minmax(320px, 1fr)",
    sidePanelWidth: "470px",
    sidePanelPaddingX: "20px",
    sidePanelCenterPadding: "clamp(52px, 4.5vw, 110px)",
    sidePanelContentMaxWidth: "420px",
    topLogoWidth: 170,
    topLogoHeight: 102,
    topLogoTop: 28,
    panelLabelSize: "15px",
    panelTextSize: "15px",
    panelHeadingSize: "18px",
    panelActionTextSize: "15px",
    avatarSize: 40,
    headingSize: "clamp(38px, 3.6vw, 54px)",
    headingTranslateY: "calc(-50% + 44px)",
    headingArrowHeight: 64,
    headingArrowWidth: 48,
    headingArrowMarginTop: 12,
    bonVoyageTop: "40%",
    bonVoyageLabelSize: "clamp(20px, 1.8vw, 28px)",
    bonVoyageTitleSize: "clamp(34px, 3vw, 50px)",
    buttonSize: "clamp(260px, 20vw, 350px)",
    buttonMinSize: 260,
    buttonTranslateY: "16%",
    magnetPadding: 96,
  };
};

// Fallback websites in case Google Sheets fetch fails
const FALLBACK_WEBSITES = [
  "https://www.are.na",
  "https://www.thispersondoesnotexist.com",
  "https://www.radio.garden",
  "https://www.internetlivestats.com",
  "https://www.howmanypeopleareinspacerightnow.com",
  "https://www.thewikigame.com",
  "https://www.radiooooo.com",
  "https://www.earth.fm",
  "https://www.zoomquilt.org",
];

// Cookie management functions
const getVisitedSites = (): string[] => {
  const visitedSitesCookie = document.cookie
    .split("; ")
    .find((row) => row.startsWith("visitedSites="));
  if (visitedSitesCookie) {
    try {
      const parsed = JSON.parse(decodeURIComponent(visitedSitesCookie.split("=")[1]));
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const clearVisitedSites = () => {
  document.cookie = `visitedSites=${encodeURIComponent(
    JSON.stringify([])
  )}; path=/; SameSite=Lax`;
};

const addVisitedSite = (site: string) => {
  const visitedSites = getVisitedSites();
  if (!visitedSites.includes(site)) {
    visitedSites.push(site);
  }
  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + 30);
  document.cookie = `visitedSites=${encodeURIComponent(
    JSON.stringify(visitedSites)
  )}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
};

const setCookieConsent = (consent: boolean) => {
  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + 365);
  document.cookie = `cookieConsent=${consent}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
};

const hasRecordedCookieChoice = (): boolean =>
  document.cookie.split("; ").some((row) => row.startsWith("cookieConsent="));

const App: React.FC = () => {
  const [websites, setWebsites] = useState<Website[]>([]);
  const [showCookieConsent, setShowCookieConsent] = useState(false);
  const [usefulStuffEnabled, setUsefulStuffEnabled] = useState(true);
  const [nsfwEnabled, setNsfwEnabled] = useState(false);
  const [showLastVisited, setShowLastVisited] = useState(false);
  const [showSubmitLinkPopUp, setShowSubmitLinkPopUp] = useState(false);
  const [showFeedbackPopUp, setShowFeedbackPopUp] = useState(false);
  const [lastVisitedSite, setLastVisitedSite] = useState<Website | null>(null);
  const [animationPhase, setAnimationPhase] = useState<"idle" | "launching">(
    "idle"
  );
  const [starBurst, setStarBurst] = useState(false);
  const [showManifestoPopup, setShowManifestoPopup] = useState(false);
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [shareLinkCopied, setShareLinkCopied] = useState(false);
  const [pressCount, setPressCount] = useState<number | null>(null);
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [trapezoidBaseWidth, setTrapezoidBaseWidth] = useState(0.55); // 70% width by default
  const [trapezoidTopWidth, setTrapezoidTopWidth] = useState(0.1); // 10% width by default
  const [trapezoidBlur, setTrapezoidBlur] = useState(40); // Blur intensity
  const [trapezoidFilterRegion, setTrapezoidFilterRegion] = useState(40); // Filter region percentage
  const [showBonVoyage, setShowBonVoyage] = useState(false); // Track when to show BON VOYAGE text
  // Returning visitors (cookie choice already stored): show tribute intro first — start overlay visible to avoid a flash of the main screen.
  const hasCookieChoiceOnLoad = hasRecordedCookieChoice();
  const [showIntroOverlay, setShowIntroOverlay] = useState(hasCookieChoiceOnLoad);
  const [introOverlayVisible, setIntroOverlayVisible] = useState(hasCookieChoiceOnLoad);
  const [introTextVisible, setIntroTextVisible] = useState(false);
  const [introTextExiting, setIntroTextExiting] = useState(false);
  const [hoveredContributor, setHoveredContributor] = useState<string | null>(null);
  const [contributorAnchorRect, setContributorAnchorRect] = useState<DOMRect | null>(null);
  const [pipHandle, setPipHandle] = useState<PipHandle | null>(null);
  const [smolBtnIn, setSmolBtnIn] = useState(false);
  const launchButtonRef = useRef<AdventureButtonHandle>(null);
  const timeoutIdsRef = useRef<number[]>([]);

  const contributorVideos = useMemo<Record<string, string>>(() => ({
    "Arun Koushik": arunVideo,
    "Saurav Banerjee": sauravVideo,
    "Ruhi Panjwani": ruhiVideo,
  }), []);

  const contributorLinks = useMemo<Record<string, string>>(() => ({
    "Arun Koushik": "https://www.youhavereachedarun.com/",
    "Ruhi Panjwani": "https://www.linkedin.com/in/ruhipanjwani",
    "Saurav Banerjee": "https://souravbanerjee599-web.github.io/Portfolio",
  }), []);

  const handleContributorHover = (name: string, e: React.MouseEvent) => {
    setHoveredContributor(name);
    setContributorAnchorRect((e.currentTarget as HTMLElement).getBoundingClientRect());
  };

  const handleContributorLeave = () => {
    setHoveredContributor(null);
  };

  // Button bounce animation
  const [buttonBounceProps, buttonBounceApi] = useSpring(() => ({
    scale: 1,
    config: { tension: 420, friction: 20 },
  }));

  // Trapezoid pathway animation
  const [trapezoidProps, trapezoidApi] = useSpring(() => ({
    opacity: 0,
    height: 0, // Start with 0 height
    top: 0,
    config: { tension: 300, friction: 20, mass: 1 }, // Original config
  }));

  const [trapezoidTextProps, trapezoidTextApi] = useSpring(() => ({
    opacity: 0,
    y: 200,
    config: { tension: 120, friction: 28 },
  }));
  const desktopLayout = getDesktopLayout(viewportWidth);
  const runwayTopWidthPx =
    viewportWidth * ((RUNWAY_TOP_RIGHT - RUNWAY_TOP_LEFT) / 100);
  const topSigilWidth = Math.min(
    desktopLayout.topLogoWidth,
    Math.max(92, runwayTopWidthPx * 0.74)
  );
  const topSigilHeight =
    (topSigilWidth / desktopLayout.topLogoWidth) * desktopLayout.topLogoHeight;

  const clearScheduledActions = useCallback(() => {
    timeoutIdsRef.current.forEach((id) => window.clearTimeout(id));
    timeoutIdsRef.current = [];
  }, []);

  const waitFor = useCallback(
    (delay: number) =>
      new Promise<void>((resolve) => {
        const id = window.setTimeout(() => {
          timeoutIdsRef.current = timeoutIdsRef.current.filter(
            (timeoutId) => timeoutId !== id
          );
          resolve();
        }, delay);

        timeoutIdsRef.current.push(id);
      }),
    []
  );

  const playIntroOverlay = useCallback(async () => {
    setShowIntroOverlay(true);
    setIntroOverlayVisible(true);
    setIntroTextVisible(false);
    setIntroTextExiting(false);

    await waitFor(100);
    setIntroTextVisible(true);

    await waitFor(1600);
    setIntroTextVisible(false);
    setIntroTextExiting(true);

    await waitFor(220);
    setIntroOverlayVisible(false);

    await waitFor(600);
    setShowIntroOverlay(false);
    setIntroTextExiting(false);
  }, [waitFor]);

  const resetAnimations = useCallback(() => {
    clearScheduledActions();
    const fadeConfig = { tension: 140, friction: 22 };
    buttonBounceApi.start({ scale: 1, config: fadeConfig });
    trapezoidApi.start({ opacity: 0, height: 0, top: 0, config: fadeConfig });
    trapezoidTextApi.start({ opacity: 0, y: 60, config: fadeConfig });
    setShowBonVoyage(false);
    setStarBurst(false);
    launchButtonRef.current?.reset();
  }, [buttonBounceApi, clearScheduledActions, trapezoidApi, trapezoidTextApi]);

  const pickRandomWebsite = useCallback((): Website | undefined => {
    if (websites.length === 0) return undefined;

    const now = new Date();

    const eligible = websites.filter((website) => {
      if (website.isNsfw && !nsfwEnabled) return false;
      if (website.isUseful && !usefulStuffEnabled) return false;
      if (website.expiryDate && website.expiryDate < now) return false;
      return true;
    });

    const pool = eligible.length > 0 ? eligible : websites;

    const visited = getVisitedSites();
    const unseen = pool.filter((w) => !visited.includes(w.url));

    const finalPool = unseen.length > 0 ? unseen : (() => {
      clearVisitedSites();
      return pool;
    })();

    return finalPool[Math.floor(Math.random() * finalPool.length)];
  }, [nsfwEnabled, usefulStuffEnabled, websites]);

  const sendToAdventure = useCallback((website: Website) => {
    const opened = window.open(website.url, "_blank");

    if (opened) {
      opened.opener = null;
      return;
    }

    window.location.assign(website.url);
  }, []);

  useEffect(() => {
    if (!hasRecordedCookieChoice()) {
      setShowCookieConsent(true);
      return;
    }
    void playIntroOverlay();
  }, [playIntroOverlay]);

  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const refreshPressCount = () => {
      void fetchButtonPressCount().then((count) => {
        if (!cancelled && count !== null) setPressCount(displayPressCount(count));
      });
    };

    refreshPressCount();

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        refreshPressCount();
      }
    };
    const handleFocus = () => refreshPressCount();

    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleFocus);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  useEffect(() => {
    if (showIntroOverlay || showCookieConsent) return;
    const id = window.setTimeout(() => setSmolBtnIn(true), 800);
    return () => clearTimeout(id);
  }, [showIntroOverlay, showCookieConsent]);

  useEffect(() => {
    const fetchWebsites = async () => {
      try {
        const response = await fetch(SHEET_URL);
        if (!response.ok) {
          throw new Error("Failed to fetch from Google Sheets");
        }
        const csvText = await response.text();

        // Parse CSV — first row is headers, use them to find columns by name
        const rows = csvText.split("\n");
        const headers = rows[0].split(",").map((h) => h.replace(/["']/g, "").trim().toLowerCase());

        const col = (name: string) => headers.findIndex((h) => h.includes(name));
        const idxActive  = col("active");
        const idxUrl     = col("link") !== -1 ? col("link") : col("url");
        const idxNsfw    = col("nsfw");
        const idxUseful  = col("useful");
        const idxTitle   = col("title") !== -1 ? col("title") : col("name");
        const idxExpiry  = col("expir");
        const idxSubmittedBy = col("submitted") !== -1 ? col("submitted") : col("nickname");

        const parseCell = (columns: string[], idx: number) =>
          idx >= 0 ? columns[idx]?.replace(/["']/g, "").trim() : "";

        const parseDate = (val: string): Date | null => {
          if (!val) return null;
          const d = new Date(val);
          return isNaN(d.getTime()) ? null : d;
        };

        const parseCSVRow = (line: string): string[] => {
          const result: string[] = [];
          let current = "";
          let inQuotes = false;
          for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (ch === '"') {
              inQuotes = !inQuotes;
            } else if (ch === "," && !inQuotes) {
              result.push(current.trim());
              current = "";
            } else {
              current += ch;
            }
          }
          result.push(current.trim());
          return result;
        };

        const websiteList = rows
          .slice(1) // Skip header row
          .map((line) => {
            const columns = parseCSVRow(line);

            const activeVal = parseCell(columns, idxActive);
            if (!activeVal.toLowerCase().includes("yes")) return null;

            const rawUrl = parseCell(columns, idxUrl);
            if (!rawUrl) return null;
            const url = rawUrl.startsWith("http") ? rawUrl : `https://${rawUrl}`;

            return {
              url,
              isNsfw:   parseCell(columns, idxNsfw).toLowerCase().includes("yes"),
              isUseful: parseCell(columns, idxUseful).toLowerCase().includes("yes"),
              title:    parseCell(columns, idxTitle) || "Hope you liked visiting this site! :)",
              expiryDate: parseDate(parseCell(columns, idxExpiry)),
              submittedBy: parseCell(columns, idxSubmittedBy) || undefined,
            } as Website;
          })
          .filter((website): website is Website => website !== null);

        // Only update if we got valid websites from the sheet
        if (websiteList.length > 0) {
          setWebsites(websiteList);
        }
      } catch (error) {
        console.error("Error fetching websites:", error);
        // Convert fallback websites to the new format
        setWebsites(
          FALLBACK_WEBSITES.map((url) => ({
            url,
            isNsfw: false,
            isUseful: false,
            title: url,
          }))
        );
      } finally {
        // loading complete
      }
    };

    fetchWebsites();
  }, []);

  useEffect(() => () => clearScheduledActions(), [clearScheduledActions]);

  const handleButtonClick = async () => {
    if (websites.length === 0 || animationPhase !== "idle") return;

    const randomSite = pickRandomWebsite();
    if (!randomSite) return;

    trackEvent("main_adventure_click");
    recordButtonPress("main");
    setPressCount((prev) =>
      prev === null ? displayPressCount(1) : prev + 1
    );
    setAnimationPhase("launching");
    setShowBonVoyage(false);
    setStarBurst(false);

    // Trapezoid `height` is in viewBox units (0…RUNWAY_TRAP_VIEWBOX), not CSS pixels — using
    // innerHeight here left a gap on shorter viewports (e.g. MacBook Air) because 900px ≠ 1000 units.
    const vb = RUNWAY_TRAP_VIEWBOX;

    // Match the black center pathway shape (30%-70% base, 45%-55% top)
    setTrapezoidBaseWidth(0.40);
    setTrapezoidTopWidth(0.10);
    setTrapezoidBlur(18);
    setTrapezoidFilterRegion(20);

    launchButtonRef.current?.playFromStart();

    // Button press — quick physical response
    buttonBounceApi.start({
      to: [
        { scale: 0.94, config: { tension: 650, friction: 20 } },
        { scale: 1.015, config: { tension: 420, friction: 16 } },
        { scale: 1, config: { tension: 320, friction: 20 } },
      ],
    });

    await waitFor(130);

    trapezoidApi.start({
      opacity: 1,
      height: vb * 0.24,
      top: 0,
      config: { tension: 150, friction: 24, mass: 1.1 },
    });

    await waitFor(220);
    trapezoidApi.start({
      height: vb * 0.05,
      config: { tension: 360, friction: 18 },
    });

    await waitFor(160);
    setStarBurst(true);
    setTrapezoidBlur(22);
    setTrapezoidFilterRegion(24);

    trapezoidApi.start({
      height: vb,
      top: 0,
      config: { tension: 480, friction: 15, mass: 0.7 },
    });

    await waitFor(90);
    setShowBonVoyage(true);
    trapezoidTextApi.start({
      opacity: 1,
      y: 0,
      config: { tension: 280, friction: 18 },
    });

    // Let the departure beat land before the new tab opens.
    await waitFor(620);
    setLastVisitedSite(randomSite);
    setShowLastVisited(true);
    addVisitedSite(randomSite.url);
    recordLinkShown(randomSite.url);
    sendToAdventure(randomSite);

    await waitFor(360);
    resetAnimations();
    setAnimationPhase("idle");
  };

  const handleCookieConsent = async (accepted: boolean) => {
    trackEvent("cookie_choice", { accepted });
    setCookieConsent(accepted);
    setShowCookieConsent(false);
    await playIntroOverlay();
  };

  const handleLastVisitedClose = () => {
    setShowLastVisited(false);
  };

  useEffect(() => {
    const phrases = [
      "Press me, cupcakes.  ✦  ",
      "Totally safe. Probably.  ✦  ",
      "Hot button near you.  ✦  ",
    ];
    let phraseIndex = 0;
    let offset = 0;
    const id = window.setInterval(() => {
      const text = phrases[phraseIndex];
      document.title = text.slice(offset) + text.slice(0, offset);
      offset = (offset + 1) % text.length;
      if (offset === 0) phraseIndex = (phraseIndex + 1) % phrases.length;
    }, 200);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        resetAnimations();
        setAnimationPhase("idle");
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, [resetAnimations]);

  useEffect(() => {
    if (!pipHandle) return;
    const onClose = () => {
      setPipHandle(null);
      void fetchButtonPressCount().then((count) => {
        if (count !== null) setPressCount(displayPressCount(count));
      });
    };
    pipHandle.pipWindow.addEventListener("pagehide", onClose);
    return () => pipHandle.pipWindow.removeEventListener("pagehide", onClose);
  }, [pipHandle]);

  // Mobile / tablet gate
  if (viewportWidth < 1024) {
    const isTablet = viewportWidth >= 768;
    const headingLine2 = isTablet ? "is that a tablet?" : "is that a phone?";

    return (
      <div className="relative overflow-hidden bg-black flex flex-col items-center px-8 text-center" style={{ minHeight: "100vh" }}>
        <StarField bursting={false} />

        {/* Text content — sits at top, in normal flow */}
        <div className="relative z-10 flex flex-col items-center gap-6 w-full max-w-sm" style={{ paddingTop: "clamp(64px, 14vw, 120px)" }}>
          <h1
            className="text-white leading-tight"
            style={{
              fontFamily: "'Kurdis Black', sans-serif",
              fontSize: "clamp(30px, 9vw, 42px)",
              filter: "drop-shadow(0 0 12px rgba(255,255,255,0.5))",
            }}
          >
            Ewww,
            <br />
            {headingLine2}
          </h1>

          <p
            className="text-white/70 leading-relaxed gilroy-regular"
            style={{ fontSize: "clamp(15px, 4.2vw, 18px)" }}
          >
            Most of the internet worth wandering
            <br />
            was built by people who forgot their phones.
          </p>

          <p
            className="text-white/70 leading-relaxed gilroy-regular"
            style={{ fontSize: "clamp(15px, 4.2vw, 18px)" }}
          >
            Cursor trails drifting. Hover effects alive.
            <br />
            A little chaos. A little magic.
          </p>

          <p
            className="text-white/70 leading-relaxed gilroy-regular"
            style={{ fontSize: "clamp(15px, 4.2vw, 18px)" }}
          >
            Sit with a friend.
            <br />
            Press the button the right way.
          </p>
        </div>

        {/* Dino + laptop — in normal flow, mt-auto pushes it to bottom, always below text */}
        <div
          className="relative z-10 mt-auto flex flex-col items-center"
          style={{ paddingBottom: 220 }}
        >
          <div className="relative">
            <img
              src={laptopImage}
              alt="Get a laptop"
              className="animate-oh-shit-float"
              style={{ width: "clamp(220px, 60vw, 340px)", height: "auto", display: "block" }}
            />
            <img
              src={dinoImage}
              alt="Dino"
              style={{
                width: 56,
                height: 56,
                transform: "scaleY(-1) rotate(12deg)",
                position: "absolute",
                bottom: -40,
                left: -12,
              }}
            />
          </div>
        </div>

        {/* White glowing semicircle */}
        <div
          className="absolute left-1/2 -translate-x-1/2"
          style={{ bottom: -1370, zIndex: 5 }}
        >
          <div style={{ position: "absolute", width: 1516, height: 1516, borderRadius: "50%", background: "#FFF", filter: "blur(30px)", opacity: 0.8 }} />
          <div style={{ position: "relative", width: 1516, height: 1516, borderRadius: "50%", background: "#FFF", zIndex: 1 }} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-black">
      {/* Intro Overlay */}
      {showIntroOverlay && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden"
        >
          <div
            className={`absolute inset-0 transition-opacity duration-[920ms] ease-[cubic-bezier(0.32,0,0.18,1)] ${
              introOverlayVisible ? "opacity-100" : "opacity-0"
            }`}
            style={{ background: "rgba(0,0,0,0.48)" }}
          />
          <div
            className={`absolute inset-0 scale-[1.03] transition-opacity duration-[920ms] ease-[cubic-bezier(0.32,0,0.18,1)] ${
              introOverlayVisible ? "opacity-100" : "opacity-0"
            }`}
            style={{
              backdropFilter: "blur(16px)",
              WebkitBackdropFilter: "blur(16px)",
              background:
                "radial-gradient(circle at 50% 54%, rgba(255,255,255,0.07), transparent 18%), rgba(0,0,0,0.46)",
            }}
          />
          <div
            className={`relative z-10 flex flex-col items-center px-8 text-center text-white transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] transform ${
              introTextVisible
                ? "translate-y-0 opacity-100 scale-100"
                : introTextExiting
                ? "-translate-y-8 opacity-0 scale-[0.985]"
                : "translate-y-10 opacity-0 scale-[0.985]"
            }`}
            style={{
              textShadow:
                "0 0 18px rgba(255,255,255,0.58), 0 0 52px rgba(255,255,255,0.24), 0 0 120px rgba(255,255,255,0.16)",
            }}
          >
            <div
              style={{
                fontFamily: "'Kurdis Black', sans-serif",
                fontSize: "clamp(36px, 4.2vw, 58px)",
                lineHeight: 0.92,
                letterSpacing: "-0.02em",
              }}
            >
              <div className="font-normal">A tribute to the</div>
              <div className="intro-glow-text">internet we love.</div>
            </div>
          </div>
        </div>
      )}
      {showCookieConsent && (
        <CookieConsent
          onAccept={() => handleCookieConsent(true)}
          onDecline={() => handleCookieConsent(false)}
        />
      )}
      {showLastVisited && lastVisitedSite && (
        <LastVisitedPopup
          website={lastVisitedSite}
          onClose={handleLastVisitedClose}
        />
      )}
      {hoveredContributor && contributorVideos[hoveredContributor] && (
        <RetroVideoPlayer
          videoSrc={contributorVideos[hoveredContributor]}
          name={hoveredContributor}
          visible={true}
          anchorRect={contributorAnchorRect}
        />
      )}
      {showSubmitLinkPopUp && (
        <SubmitLinkPopUp onClose={() => setShowSubmitLinkPopUp(false)} />
      )}
      {showFeedbackPopUp && (
        <FeedbackPopUp onClose={() => setShowFeedbackPopUp(false)} />
      )}
      {showManifestoPopup && (
        <ManifestoPopup onClose={() => setShowManifestoPopup(false)} />
      )}
      {showSharePopup && (
        <RightSideCardShell
          onClose={() => { setShowSharePopup(false); setShareLinkCopied(false); }}
          widthClassName="w-[350px]"
          heightClassName="h-auto"
          panelStyle={{ height: "auto", maxHeight: "calc(100vh - 56px)" }}
          side="left"
        >
          {(requestClose) => (
            <AnimatedRainbowBorder className="gilroy-medium" contentClassName="p-6">
              <div className="flex justify-between items-start pb-4">
                <div className="text-xl text-black" style={{ fontFamily: "'Kurdis Bold'" }}>
                  Blow your
                  <br />
                  friend&apos;s mind.
                </div>
                <button
                  onClick={() => { requestClose(); setShareLinkCopied(false); }}
                  className="text-black rounded-full h-[24px] w-[24px] flex items-center justify-center border border-black shrink-0"
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </button>
              </div>
              <p className="text-[14px] text-[#343434] gilroy-regular mb-6 leading-relaxed">
                Know someone who&apos;s doom-scrolling
                <br />
                their life away? Send them the antidote.
              </p>

              <div className="flex flex-col gap-3 w-full">
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
                    shareInviteBody(withoutHttpUrl(SITE_URL))
                  )}`}
                  onClick={() => trackEvent("share_outbound", { channel: "twitter" })}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full text-center px-4 py-3 bg-black text-white rounded-[10px] hover:opacity-80 transition-opacity gilroy-medium text-[14px] no-underline"
                >
                  Blow minds on X / Twitter
                </a>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(
                    shareInviteBody(withoutHttpUrl(SITE_URL))
                  )}`}
                  onClick={() => trackEvent("share_outbound", { channel: "whatsapp" })}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full text-center px-4 py-3 bg-[#25D366] text-black rounded-[10px] hover:opacity-80 transition-opacity gilroy-semibold text-[14px] no-underline"
                >
                  Send via WhatsApp
                </a>
                <a
                  href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                    SITE_URL
                  )}`}
                  onClick={() => trackEvent("share_outbound", { channel: "linkedin" })}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full text-center px-4 py-3 bg-[#0A66C2] text-white rounded-[10px] hover:opacity-80 transition-opacity gilroy-medium text-[14px] no-underline"
                >
                  LinkedIn? Why not?
                </a>
                <button
                  onClick={() => {
                    trackEvent("share_outbound", { channel: "copy_link" });
                    navigator.clipboard.writeText(shareInviteBody(withoutHttpUrl(SITE_URL))).catch(() => {});
                    setShareLinkCopied(true);
                    setTimeout(() => setShareLinkCopied(false), 2000);
                  }}
                  className="w-full px-4 py-3 bg-white text-[#343434] border border-[#ddd] rounded-[10px] hover:bg-gray-50 transition-all gilroy-medium text-[14px] cursor-pointer flex items-center justify-center gap-2"
                >
                  {shareLinkCopied ? (
                    <>
                      <CheckRoundedIcon sx={{ fontSize: 16 }} />
                      Copied! Now go blow minds.
                    </>
                  ) : (
                    "Copy link"
                  )}
                </button>
              </div>

              <p className="text-[11px] text-[#999] gilroy-regular text-center mt-5">
                The internet was made for sharing. Do the right thing.
              </p>
            </AnimatedRainbowBorder>
          )}
        </RightSideCardShell>
      )}
      <StarField bursting={starBurst} />

      <div className="pointer-events-none relative h-screen w-full overflow-hidden">
        {/* Triangles */}
        <div className="pointer-events-none absolute inset-0 z-10">
          {/* Left Side Triangles */}
          <div
            className="absolute top-0 left-0 w-full h-full bg-white"
            style={{
              clipPath: `polygon(0 30%, 50% 50%, 0 70%)`,
            }}
          ></div>
          <div
            className="absolute top-0 left-0 w-full h-full"
            style={{
              clipPath: "polygon(0 7%, 50% 50%, 0 27%)",
              backgroundColor: "#767676",
            }}
          ></div>
          <div
            className="absolute top-0 left-0 w-full h-full"
            style={{
              clipPath: "polygon(0 73%, 50% 50%, 0 93%)",
              backgroundColor: "#CCCCCB",
            }}
          ></div>

          {/* Right Side Triangles */}
          <div
            className="absolute top-0 left-0 w-full h-full bg-white"
            style={{ clipPath: "polygon(100% 30%, 50% 50%, 100% 70%)" }}
          ></div>
          <div
            className="absolute top-0 left-0 w-full h-full"
            style={{
              clipPath: "polygon(100% 7%, 50% 50%, 100% 27%)",
              backgroundColor: "#767676",
            }}
          ></div>
          <div
            className="absolute top-0 left-0 w-full h-full"
            style={{
              clipPath: "polygon(100% 73%, 50% 50%, 100% 93%)",
              backgroundColor: "#CCCCCB",
            }}
          ></div>
        </div>

        {/* Spotlight Effect */}
        <div className="pointer-events-none absolute inset-0 z-20">
          {/* Left diagonal line */}
          <div className="absolute top-0 left-0 w-full h-full">
            <div
              className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-transparent via-transparent to-black"
              style={{ clipPath: "polygon(0 0, 0 100%, 45% 100%, 0 0)" }}
            ></div>
          </div>

          {/* Right diagonal line */}
          <div className="absolute top-0 right-0 w-full h-full">
            <div
              className="absolute top-0 right-0 w-full h-full bg-gradient-to-bl from-transparent via-transparent to-black"
              style={{
                clipPath: "polygon(100% 0, 100% 100%, 55% 100%, 100% 0)",
              }}
            ></div>
          </div>

          {/* Center spotlight area */}
          <div className="absolute top-0 left-0 w-full h-full">
            {/* White border (slightly larger) */}
            <div
              className="absolute top-0 left-0 w-full h-full bg-white"
              style={{
                clipPath: `polygon(${RUNWAY_TOP_LEFT - RUNWAY_BORDER_OFFSET}% 0, ${
                  RUNWAY_TOP_RIGHT + RUNWAY_BORDER_OFFSET
                }% 0, ${RUNWAY_BOTTOM_RIGHT + RUNWAY_BORDER_OFFSET}% 100%, ${
                  RUNWAY_BOTTOM_LEFT - RUNWAY_BORDER_OFFSET
                }% 100%)`,
              }}
            ></div>

            {/* Black center (slightly smaller) */}
            <div
              className="absolute top-0 left-0 w-full h-full bg-black"
              style={{
                clipPath: `polygon(${RUNWAY_TOP_LEFT}% 0, ${RUNWAY_TOP_RIGHT}% 0, ${RUNWAY_BOTTOM_RIGHT}% 100%, ${RUNWAY_BOTTOM_LEFT}% 100%)`,
              }}
            ></div>
          </div>

          {/* Glowy Magical Trapezoid - SVG with blur for soft, glowy edges (aligned to pathway) */}
          <animated.svg
            width="100%"
            height="100vh"
            style={{
              position: "absolute",
              left: 0,
              bottom: 0,
              zIndex: 1000,
              pointerEvents: "none",
              opacity: trapezoidProps.opacity,
            }}
            viewBox={`0 0 ${RUNWAY_TRAP_VIEWBOX} ${RUNWAY_TRAP_VIEWBOX}`}
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="trapezoidGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#F4958E" />
                <stop offset="20%" stopColor="#FFAAF7" />
                <stop offset="40%" stopColor="#9193FC" />
                <stop offset="60%" stopColor="#B4F6FE" />
                <stop offset="80%" stopColor="#B0FFC0" />
                <stop offset="90%" stopColor="#FEFF9D" />
                <stop offset="100%" stopColor="#F3D9A0" />
              </linearGradient>
              <filter
                id="blurMe"
                x={`-${trapezoidFilterRegion}%`}
                y={`-${trapezoidFilterRegion}%`}
                width={`${100 + 2 * trapezoidFilterRegion}%`}
                height={`${100 + 2 * trapezoidFilterRegion}%`}
              >
                <feGaussianBlur stdDeviation={trapezoidBlur} />
              </filter>
            </defs>
            <animated.polygon
              fill="url(#trapezoidGradient)"
              filter="url(#blurMe)"
              points={trapezoidProps.height.to((h) => {
                const W = RUNWAY_TRAP_VIEWBOX;
                const baseWidth = trapezoidBaseWidth; // 0.7 for 70%, 0.4 for 40%, etc.
                const topWidth = trapezoidTopWidth; // 0.1 for 10%, etc.
                const baseLeftX = (0.5 - baseWidth / 2) * W;
                const baseRightX = (0.5 + baseWidth / 2) * W;
                const topLeftX = (0.5 - topWidth / 2) * W;
                const topRightX = (0.5 + topWidth / 2) * W;
                // Animate only the y of the top points (h in same units as viewBox height)
                const topY = RUNWAY_TRAP_VIEWBOX - h;
                return `
                  ${baseLeftX},1000
                  ${baseRightX},1000
                  ${topRightX},${topY}
                  ${topLeftX},${topY}
                `;
              })}
            />
          </animated.svg>
        </div>

        {/* Content */}
        <div className="pointer-events-none relative z-30 flex h-full flex-col">
          {/* Header */}
          <img
            src={boredHumans}
            alt="Bored Humans"
            className="absolute left-1/2 -translate-x-1/2 transform"
            style={{
              top: `${desktopLayout.topLogoTop}px`,
              width: `${topSigilWidth}px`,
              height: `${topSigilHeight}px`,
              mixBlendMode: "screen",
            }}
          />

          {/* Main Content */}
          <div className="flex h-[calc(100vh-80px)] flex-1 items-stretch">
            <div
              className="grid w-full gap-0"
              style={{ gridTemplateColumns: desktopLayout.gridTemplateColumns }}
            >
              {/* Left Column */}
              <div
                className="relative z-30 flex h-full flex-col justify-center text-black"
                style={{ paddingLeft: desktopLayout.sidePanelPaddingX, paddingRight: desktopLayout.sidePanelCenterPadding }}
              >
                {desktopLayout.isCompact ? (
                  <div
                    className="pointer-events-auto flex w-full items-center justify-between gap-4 gilroy-medium"
                  >
                    <div className="min-w-0">
                      <div className="mb-6">
                        <h2
                          className="mb-2 gilroy-regular"
                          style={{ fontSize: desktopLayout.panelLabelSize }}
                        >
                          Creator
                        </h2>
                        <a
                          href={contributorLinks["Arun Koushik"]}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group flex flex-row items-center gap-2 text-nowrap cursor-pointer"
                          onMouseEnter={(e) => handleContributorHover("Arun Koushik", e)}
                          onMouseLeave={handleContributorLeave}
                          onClick={() => trackEvent("contributor_click", { name: "Arun Koushik" })}
                        >
                          <img
                            src={arunsDucky}
                            alt="Arun Koushik"
                            style={{
                              width: `${desktopLayout.avatarSize}px`,
                              height: `${desktopLayout.avatarSize}px`,
                            }}
                          />
                          <span
                            className="gilroy-regular transition-all duration-200 hover-underline group-hover-underline group-hover:opacity-60"
                            style={{ fontSize: desktopLayout.panelTextSize }}
                          >
                            Arun Koushik
                          </span>
                        </a>
                      </div>

                      <div>
                        <h2
                          className="mb-2 gilroy-regular"
                          style={{ fontSize: desktopLayout.panelLabelSize }}
                        >
                          Contributors
                        </h2>
                        <div className="space-y-3">
                          <a
                            href={contributorLinks["Ruhi Panjwani"]}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex flex-row items-center gap-2 text-nowrap cursor-pointer"
                            onMouseEnter={(e) => handleContributorHover("Ruhi Panjwani", e)}
                            onMouseLeave={handleContributorLeave}
                            onClick={() => trackEvent("contributor_click", { name: "Ruhi Panjwani" })}
                          >
                            <img
                              src={ruhisPanda}
                              alt="Ruhi Panjwani"
                              style={{
                                width: `${desktopLayout.avatarSize}px`,
                                height: `${desktopLayout.avatarSize}px`,
                              }}
                            />
                            <span
                              className="gilroy-regular transition-all duration-200 hover-underline group-hover-underline group-hover:opacity-60"
                              style={{ fontSize: desktopLayout.panelTextSize }}
                            >
                              Ruhi Panjwani
                            </span>
                          </a>
                          <a
                            href={contributorLinks["Saurav Banerjee"]}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex flex-row items-center gap-2 text-nowrap cursor-pointer"
                            onMouseEnter={(e) => handleContributorHover("Saurav Banerjee", e)}
                            onMouseLeave={handleContributorLeave}
                            onClick={() => trackEvent("contributor_click", { name: "Saurav Banerjee" })}
                          >
                            <img
                              src={sauravsElephant}
                              alt="Saurav Banerjee"
                              style={{
                                width: `${desktopLayout.avatarSize}px`,
                                height: `${desktopLayout.avatarSize}px`,
                              }}
                            />
                            <span
                              className="gilroy-regular transition-all duration-200 hover-underline group-hover-underline group-hover:opacity-60"
                              style={{ fontSize: desktopLayout.panelTextSize }}
                            >
                              Saurav Banerjee
                            </span>
                          </a>
                        </div>
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <h2
                        className="mb-2 gilroy-bold"
                        style={{ fontSize: desktopLayout.panelHeadingSize }}
                      >
                        Share
                      </h2>
                      <button
                        className="flex items-center gap-1.5 text-nowrap rounded-[999px] border border-black gilroy-regular transition-all duration-200 hover:bg-black hover:text-white group cursor-pointer"
                        style={{
                          padding: "8px 12px",
                          fontSize: desktopLayout.panelActionTextSize,
                        }}
                        onClick={() => {
                          trackEvent("share_panel_open");
                          setShowSharePopup(true);
                        }}
                      >
                        <img src={bombIcon} alt="" className="transition-all duration-200 group-hover:invert" style={{ width: 17, height: 17 }} />
                        <span style={{ fontSize: desktopLayout.panelActionTextSize }}>
                          Blow your friend&apos;s mind
                        </span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    className="pointer-events-auto flex w-full items-center justify-between gap-8 gilroy-medium"
                  >
                    <div className="min-w-0">
                      <div className="mb-6">
                        <h2
                          className="mb-2 gilroy-regular"
                          style={{ fontSize: desktopLayout.panelLabelSize }}
                        >
                          Creator
                        </h2>
                        <a
                          href={contributorLinks["Arun Koushik"]}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group flex flex-row items-center gap-2 text-nowrap cursor-pointer"
                          onMouseEnter={(e) => handleContributorHover("Arun Koushik", e)}
                          onMouseLeave={handleContributorLeave}
                          onClick={() => trackEvent("contributor_click", { name: "Arun Koushik" })}
                        >
                          <img
                            src={arunsDucky}
                            alt="Arun Koushik"
                            style={{
                              width: `${desktopLayout.avatarSize}px`,
                              height: `${desktopLayout.avatarSize}px`,
                            }}
                          />
                          <span
                            className="gilroy-regular transition-all duration-200 hover-underline group-hover-underline group-hover:opacity-60"
                            style={{ fontSize: desktopLayout.panelTextSize }}
                          >
                            Arun Koushik
                          </span>
                        </a>
                      </div>

                      <div>
                        <h2
                          className="mb-2 gilroy-regular"
                          style={{ fontSize: desktopLayout.panelLabelSize }}
                        >
                          Contributors
                        </h2>
                        <div className="space-y-3">
                          <a
                            href={contributorLinks["Ruhi Panjwani"]}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex flex-row items-center gap-2 text-nowrap cursor-pointer"
                            onMouseEnter={(e) => handleContributorHover("Ruhi Panjwani", e)}
                            onMouseLeave={handleContributorLeave}
                            onClick={() => trackEvent("contributor_click", { name: "Ruhi Panjwani" })}
                          >
                            <img
                              src={ruhisPanda}
                              alt="Ruhi Panjwani"
                              style={{
                                width: `${desktopLayout.avatarSize}px`,
                                height: `${desktopLayout.avatarSize}px`,
                              }}
                            />
                            <span
                              className="gilroy-regular transition-all duration-200 hover-underline group-hover-underline group-hover:opacity-60"
                              style={{ fontSize: desktopLayout.panelTextSize }}
                            >
                              Ruhi Panjwani
                            </span>
                          </a>
                          <a
                            href={contributorLinks["Saurav Banerjee"]}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex flex-row items-center gap-2 text-nowrap cursor-pointer"
                            onMouseEnter={(e) => handleContributorHover("Saurav Banerjee", e)}
                            onMouseLeave={handleContributorLeave}
                            onClick={() => trackEvent("contributor_click", { name: "Saurav Banerjee" })}
                          >
                            <img
                              src={sauravsElephant}
                              alt="Saurav Banerjee"
                              style={{
                                width: `${desktopLayout.avatarSize}px`,
                                height: `${desktopLayout.avatarSize}px`,
                              }}
                            />
                            <span
                              className="gilroy-regular transition-all duration-200 hover-underline group-hover-underline group-hover:opacity-60"
                              style={{ fontSize: desktopLayout.panelTextSize }}
                            >
                              Saurav Banerjee
                            </span>
                          </a>
                        </div>
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <h2
                        className="mb-2 gilroy-bold"
                        style={{ fontSize: desktopLayout.panelHeadingSize }}
                      >
                        Share
                      </h2>
                      <button
                        className="flex items-center gap-2 text-nowrap rounded-[999px] border border-black gilroy-regular transition-all duration-200 hover:bg-black hover:text-white group cursor-pointer"
                        style={{
                          padding: "8px 16px",
                          fontSize: desktopLayout.panelActionTextSize,
                        }}
                        onClick={() => {
                          trackEvent("share_panel_open");
                          setShowSharePopup(true);
                        }}
                      >
                        <img src={bombIcon} alt="" className="transition-all duration-200 group-hover:invert" style={{ width: 18, height: 18 }} />
                        <span style={{ fontSize: desktopLayout.panelActionTextSize }}>
                          Blow your friend&apos;s mind
                        </span>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Center Column */}
              <div className="relative flex h-full flex-col items-center gilroy-black">
                {showBonVoyage ? (
                  <animated.div
                    className="absolute w-full text-center"
                    style={{
                      top: desktopLayout.bonVoyageTop,
                      left: 0,
                      opacity: trapezoidTextProps.opacity,
                      transform: trapezoidTextProps.y.to(
                        (y) => `translateY(${y}px)`
                      ),
                      pointerEvents: "none",
                    }}
                  >
                    <div
                      className="flex flex-col items-center gap-3 text-black"
                      style={{
                        fontFamily: "'Kurdis Regular', sans-serif",
                        fontSize: desktopLayout.bonVoyageLabelSize,
                        lineHeight: 0.92,
                        letterSpacing: "-0.01em",
                      }}
                    >
                      <img
                        src={bonVoyageArrow}
                        alt=""
                        style={{
                          width: `${desktopLayout.headingArrowWidth + 28}px`,
                          height: `${desktopLayout.headingArrowHeight + 14}px`,
                        }}
                      />
                      BON VOYAGE
                    </div>
                    <div
                      className="mt-2 text-black"
                      style={{
                        fontFamily: "'Kurdis Black', sans-serif",
                        fontSize: desktopLayout.bonVoyageTitleSize,
                        lineHeight: 0.9,
                        letterSpacing: "-0.02em",
                      }}
                    >
                      ADVENTURER
                    </div>
                  </animated.div>
                ) : (
                  <div className="absolute top-1/2 left-1/2" style={{ transform: `translateX(-50%) translateY(${desktopLayout.headingTranslateY})` }}>
                    {animationPhase === "idle" ? (
                      <div className="flex flex-col items-center gap-1">
                        <h1
                          className="text-center whitespace-nowrap leading-[0.95] text-white shiny-text"
                          style={{
                            fontSize: desktopLayout.headingSize,
                            fontFamily: "'Kurdis Black', sans-serif",
                            filter:
                              "drop-shadow(0 0 12px rgba(255,255,255,0.72)) drop-shadow(0 0 30px rgba(255,255,255,0.24)) drop-shadow(0 0 64px rgba(255,255,255,0.1))",
                          }}
                        >
                          Press the
                          <br />
                          button
                        </h1>
                        <p
                          className="text-center gilroy-regular text-white/40 leading-snug max-w-[28ch]"
                          style={{
                            fontSize: "clamp(14px, 1.15vw, 18px)",
                            marginTop: "10px",
                          }}
                        >
                          Cool corners of the web, handpicked.
                        </p>
                        <img
                          src={bounceArrow}
                          alt=""
                          className="animate-nod"
                          style={{
                            marginTop: `${desktopLayout.headingArrowMarginTop}px`,
                            height: `${desktopLayout.headingArrowHeight}px`,
                            width: `${desktopLayout.headingArrowWidth}px`,
                          }}
                        />
                      </div>
                    ) : (
                        <h1
                          className="text-center whitespace-nowrap leading-[0.95] text-white shiny-text"
                          style={{
                            fontSize: desktopLayout.headingSize,
                            fontFamily: "'Kurdis Black', sans-serif",
                            filter:
                              "drop-shadow(0 0 12px rgba(255,255,255,0.72)) drop-shadow(0 0 30px rgba(255,255,255,0.24)) drop-shadow(0 0 64px rgba(255,255,255,0.1))",
                          }}
                      >
                        Initiating <br />
                        dial-up..
                      </h1>
                    )}
                  </div>
                )}

                <div
                  className="pointer-events-auto absolute bottom-0 left-1/2 z-20"
                  style={{
                    transform: `translate(-50%, ${desktopLayout.buttonTranslateY})`,
                  }}
                >
                  <Magnet
                    padding={desktopLayout.magnetPadding}
                    magnetStrength={4.2}
                    disabled={animationPhase !== "idle"}
                  >
                    <animated.div
                      className="relative"
                      style={{
                        transform: buttonBounceProps.scale.to(
                          (scale) => `scale(${scale})`
                        ),
                      }}
                    >
                      <AdventureButton
                        ref={launchButtonRef}
                        onClick={handleButtonClick}
                        className="focus:outline-none relative cursor-pointer disabled:cursor-default"
                        style={{
                          width: desktopLayout.buttonSize,
                          height: desktopLayout.buttonSize,
                          minWidth: `${desktopLayout.buttonMinSize}px`,
                          minHeight: `${desktopLayout.buttonMinSize}px`,
                        }}
                        disabled={animationPhase !== "idle"}
                      />
                      {pressCount !== null && (
                        <div
                          className="press-count-chip pointer-events-none absolute left-1/2 z-10 whitespace-nowrap rounded-full border border-white/25 bg-black/55 px-2.5 py-1 gilroy-medium text-white/80 backdrop-blur-[2px]"
                          style={{
                            // Anchor near the visible red button crown; keep a tight ~12px gap.
                            top: "24%",
                            fontSize: "clamp(10px, 0.85vw, 12px)",
                            letterSpacing: "0.01em",
                            transform: "translate(-50%, calc(-100% - 12px))",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.22)",
                          }}
                        >
                          <span className="press-live-dot" aria-hidden="true" />
                          <span>
                            Pressed {formatPressCount(pressCount)}{" "}
                            {pressCount === 1 ? "time" : "times"}
                          </span>
                        </div>
                      )}
                    </animated.div>
                  </Magnet>
                </div>
              </div>

              {/* Right Column */}
              <div
                className="relative z-30 flex h-full flex-col justify-center text-black"
                style={{ paddingLeft: desktopLayout.sidePanelCenterPadding, paddingRight: desktopLayout.sidePanelPaddingX }}
              >
                {desktopLayout.isCompact ? (
                  <div
                    className="pointer-events-auto flex w-full items-center justify-between gap-4 text-black"
                  >
                    <div className="min-w-0 shrink-0">
                      <h2
                        className="mb-2 gilroy-bold"
                        style={{ fontSize: desktopLayout.panelHeadingSize }}
                      >
                        Can include
                      </h2>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => {
                            const next = !nsfwEnabled;
                            trackEvent("filter_nsfw", { enabled: next });
                            setNsfwEnabled(next);
                          }}
                          aria-pressed={nsfwEnabled}
                          className={`flex items-center gap-1.5 rounded-[999px] transition-all duration-200 whitespace-nowrap gilroy-regular cursor-pointer ${
                            nsfwEnabled
                              ? "bg-black text-white"
                              : "bg-white text-black border border-black hover:bg-gray-100"
                          }`}
                          style={{
                            padding: "8px 12px",
                            fontSize: desktopLayout.panelActionTextSize,
                          }}
                        >
                          {nsfwEnabled && (
                            <CheckRoundedIcon sx={{ fontSize: 17 }} />
                          )}
                          <span>NSFW</span>
                        </button>
                        <button
                          onClick={() => {
                            const next = !usefulStuffEnabled;
                            trackEvent("filter_useful", { enabled: next });
                            setUsefulStuffEnabled(next);
                          }}
                          aria-pressed={usefulStuffEnabled}
                          className={`flex items-center gap-1.5 rounded-[999px] transition-all duration-200 whitespace-nowrap gilroy-regular cursor-pointer ${
                            usefulStuffEnabled
                              ? "bg-black text-white"
                              : "bg-white text-black border border-black hover:bg-gray-100"
                          }`}
                          style={{
                            padding: "8px 12px",
                            fontSize: desktopLayout.panelActionTextSize,
                          }}
                        >
                          {usefulStuffEnabled && (
                            <CheckRoundedIcon sx={{ fontSize: 17 }} />
                          )}
                          <span>Useful stuff</span>
                        </button>
                      </div>
                    </div>
                    <div className="shrink-0">
                      <div
                        className="space-y-3 text-right whitespace-nowrap gilroy-regular"
                        style={{ fontSize: desktopLayout.panelTextSize }}
                      >
                        <div
                          className="cursor-pointer text-black transition-all duration-200 hover:opacity-60 hover-underline-right"
                          onClick={() => {
                            trackEvent("submit_link_open");
                            setShowSubmitLinkPopUp(true);
                          }}
                        >
                          Submit a link
                        </div>
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            trackEvent("feedback_click");
                            setShowFeedbackPopUp(true);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              trackEvent("feedback_click");
                              setShowFeedbackPopUp(true);
                            }
                          }}
                          className="cursor-pointer text-black transition-all duration-200 hover:opacity-60 hover-underline-right"
                        >
                          Feedback / ideas
                        </div>
                        <div
                          className="cursor-pointer text-black transition-all duration-200 hover:opacity-60 hover-underline-right"
                          onClick={() => {
                            trackEvent("manifesto_open");
                            setShowManifestoPopup(true);
                          }}
                        >
                          Our internet manifesto
                        </div>
                        {EXPERIMENT_MINI_BUTTON && isPipSupported() && smolBtnIn && (
                          <div className="flex justify-end pt-1 animate-smol-btn-entrance">
                            <div
                              className="smol-btn-rainbow gilroy-semibold"
                              onClick={async () => {
                                if (isPipOpen()) return;
                                trackEvent("smol_button_launch");
                                const handle = await openPipWindow();
                                setPipHandle(handle);
                              }}
                            >
                              <div className="smol-btn-rainbow-fill">
                                <img src={smolButtonIcon} alt="" />
                                <span>
                                  {isPipOpen() ? "Smol button launched" : "Smol button"}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div
                    className="pointer-events-auto flex w-full items-center justify-between gap-8 text-black"
                  >
                    <div className="min-w-0">
                      <h2
                        className="mb-2 gilroy-bold"
                        style={{ fontSize: desktopLayout.panelHeadingSize }}
                      >
                        Can include
                      </h2>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            const next = !nsfwEnabled;
                            trackEvent("filter_nsfw", { enabled: next });
                            setNsfwEnabled(next);
                          }}
                          aria-pressed={nsfwEnabled}
                          className={`flex items-center gap-1.5 rounded-[999px] transition-all duration-200 whitespace-nowrap gilroy-regular cursor-pointer ${
                            nsfwEnabled
                              ? "bg-black text-white"
                              : "bg-white text-black border border-black hover:bg-gray-100"
                          }`}
                          style={{
                            padding: "8px 16px",
                            fontSize: desktopLayout.panelActionTextSize,
                          }}
                        >
                          {nsfwEnabled && <CheckRoundedIcon sx={{ fontSize: 18 }} />}
                          <span>NSFW</span>
                        </button>
                        <button
                          onClick={() => {
                            const next = !usefulStuffEnabled;
                            trackEvent("filter_useful", { enabled: next });
                            setUsefulStuffEnabled(next);
                          }}
                          aria-pressed={usefulStuffEnabled}
                          className={`flex items-center gap-1.5 rounded-[999px] transition-all duration-200 whitespace-nowrap gilroy-regular cursor-pointer ${
                            usefulStuffEnabled
                              ? "bg-black text-white"
                              : "bg-white text-black border border-black hover:bg-gray-100"
                          }`}
                          style={{
                            padding: "8px 16px",
                            fontSize: desktopLayout.panelActionTextSize,
                          }}
                        >
                          {usefulStuffEnabled && (
                            <CheckRoundedIcon sx={{ fontSize: 18 }} />
                          )}
                          <span>Useful stuff</span>
                        </button>
                      </div>
                    </div>
                    <div className="shrink-0">
                      <div
                        className="space-y-3 text-right whitespace-nowrap gilroy-regular"
                        style={{ fontSize: desktopLayout.panelTextSize }}
                      >
                        <div
                          className="cursor-pointer text-black transition-all duration-200 hover:opacity-60 hover-underline-right"
                          onClick={() => {
                            trackEvent("submit_link_open");
                            setShowSubmitLinkPopUp(true);
                          }}
                        >
                          Submit a link
                        </div>
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            trackEvent("feedback_click");
                            setShowFeedbackPopUp(true);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              trackEvent("feedback_click");
                              setShowFeedbackPopUp(true);
                            }
                          }}
                          className="cursor-pointer text-black transition-all duration-200 hover:opacity-60 hover-underline-right"
                        >
                          Feedback / ideas
                        </div>
                        <div
                          className="cursor-pointer text-black transition-all duration-200 hover:opacity-60 hover-underline-right"
                          onClick={() => {
                            trackEvent("manifesto_open");
                            setShowManifestoPopup(true);
                          }}
                        >
                          Our internet manifesto
                        </div>
                        {EXPERIMENT_MINI_BUTTON && isPipSupported() && smolBtnIn && (
                          <div className="flex justify-end pt-1 animate-smol-btn-entrance">
                            <div
                              className="smol-btn-rainbow gilroy-semibold"
                              onClick={async () => {
                                if (isPipOpen()) return;
                                trackEvent("smol_button_launch");
                                const handle = await openPipWindow();
                                setPipHandle(handle);
                              }}
                            >
                              <div className="smol-btn-rainbow-fill">
                                <img src={smolButtonIcon} alt="" />
                                <span>
                                  {isPipOpen() ? "Smol button launched" : "Smol button"}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {pipHandle && pipHandle.container && createPortal(
        <PipContent
          nsfwEnabled={nsfwEnabled}
          usefulStuffEnabled={usefulStuffEnabled}
          pressCount={pressCount}
        />,
        pipHandle.container,
      )}
    </div>
  );
};

export default App;
